import { useEffect, useState } from "react";
import { kindLabel } from "@/lib/leadKind";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bot, UserRound } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const phoneFmt = (p) => { const d = (p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p; };
const BUBBLE = { lead: "bg-slate-100 text-slate-900", ava: "bg-[#0B192C] text-white ml-auto", pm: "bg-amber-100 text-slate-900 ml-auto" };

const Thread = ({ id, onChange }) => {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/pm/texts/${id}`).then((r) => { setD(r.data); onChange(); }).catch((e) => toast.error(apiError(e))); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Card className="p-8 text-sm text-slate-500">Loading…</Card>;
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.lead) setD(r.data); if (ok) toast.success(ok); onChange(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const mode = d.thread?.mode || "ai";
  const setMode = (m) => run(async () => { await api.post(`/pm/texts/${id}/mode`, { mode: m }); return api.get(`/pm/texts/${id}`); },
    m === "ai" ? "Ava will answer this lead again" : "Ava will step back");
  const blocked = d.lead.opt_out || !d.enabled || !d.lead.phone;
  return (
    <Card className="p-5" data-testid="pm-text-thread">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold text-slate-900">{d.lead.name} <span className="font-normal text-slate-500">{phoneFmt(d.lead.phone)}</span>
          <Badge variant="outline" className="ml-2 text-[10px]">{kindLabel(d.lead.kind)}</Badge></div>
        {d.thread && <Badge className={`border-0 ${mode === "ai" ? "bg-slate-900 text-white" : "bg-amber-100 text-amber-900"}`} data-testid="pm-text-mode">
          {mode === "ai" ? <><Bot className="mr-1 h-3 w-3" />Ava is answering</> : <><UserRound className="mr-1 h-3 w-3" />You're handling this</>}</Badge>}
      </div>
      <div className="mb-4 max-h-[55vh] space-y-2 overflow-y-auto">
        {!d.thread?.messages?.length && <p className="text-sm text-slate-500" data-testid="pm-text-empty">No texts with this lead yet.</p>}
        {d.thread?.messages?.map((m, i) => (
          <div key={m.id || i} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${BUBBLE[m.role] || BUBBLE.lead}`} data-testid={`pm-text-msg-${i}`}>
            <div className="whitespace-pre-wrap">{m.text}</div><div className="mt-1 text-[11px] opacity-70">{m.name} · {fmt(m.at)}</div>
          </div>
        ))}
      </div>
      {d.lead.opt_out ? <p className="text-sm text-red-700" data-testid="pm-text-optout">This lead opted out, isn't interested, or is on a Do-Not-Call list.</p>
        : !d.enabled ? <p className="text-sm text-slate-500">Texting is switched off until business texting approval comes through.</p>
        : !d.lead.phone ? <p className="text-sm text-slate-500">This lead has no phone number.</p> : null}
      {!blocked && (
        <div className="space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your own text - Ava steps back once you send" data-testid="pm-text-input" />
            <Button onClick={() => run(() => api.post(`/pm/texts/${id}/send`, { text }).then((r) => { setText(""); return r; }), "Sent")}
              disabled={busy || !text.trim()} data-testid="pm-text-send">Send</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {mode === "ai" && <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => api.post(`/pm/texts/${id}/ai`), "Ava is texting this lead")} data-testid="pm-text-ai">
              <Bot className="mr-1 h-4 w-4" />Have Ava text them</Button>}
            {d.thread && (mode === "ai"
              ? <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode("human")} data-testid="pm-text-take-over">Take over from Ava</Button>
              : <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode("ai")} data-testid="pm-text-hand-back">Hand back to Ava</Button>)}
          </div>
        </div>
      )}
    </Card>
  );
};

export default function PmTexts() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(params.get("l"));
  const load = () => { api.get("/pm/texts").then((r) => setRows(r.data.threads)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="pm-texts-page">
      <PageHeader title="Texts" subtitle="Every text Ava and you send to your leads, and their replies. Ava answers replies and books appointments; she steps back once you text." />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_1fr]">
        <Card className="p-2" data-testid="pm-text-list">
          {rows === null && <p className="p-4 text-sm text-slate-500">Loading…</p>}
          {rows && !rows.length && <p className="p-4 text-sm text-slate-500">No texts yet. Use the Text button on a lead, or the calling &amp; texting autopilot.</p>}
          {rows?.map((r) => (
            <button key={r.lead_id} type="button" onClick={() => setOpen(r.lead_id)} data-testid={`pm-text-row-${r.lead_id}`}
              className={`block w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-50 ${open === r.lead_id ? "bg-slate-100" : ""}`}>
              <div className="flex justify-between gap-2 text-sm font-semibold text-slate-900">
                <span>{r.lead_name}{r.unread && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500" />}</span>
                <span className="text-xs font-normal text-slate-400">{fmt(r.updated_at)}</span></div>
              <div className="truncate text-xs text-slate-500">{r.messages?.[0]?.text || "No messages yet"}</div>
            </button>
          ))}
        </Card>
        {open ? <Thread key={open} id={open} onChange={load} /> : <Card className="hidden p-8 text-sm text-slate-500 lg:block">Pick a conversation.</Card>}
      </div>
    </div>
  );
}
